/**
 * Pure work-order state-transition rules shared by MaintenanceProcessView and
 * useMaintenanceStateChange (calendar context menu). No React, no axios: both surfaces
 * must be able to import this without pulling in either page.
 */

const STATE_ORDER = ['quote', 'planned', 'in_progress', 'completed', 'picked_up'];
const SIDE_STATES = ['waiting', 'vehicle_in_workshop'];

/**
 * Whether moving from currentState to targetState needs a confirmation dialog.
 * Moved verbatim from MaintenanceProcessView; keep both surfaces on this one table.
 */
export function requiresConfirmation(currentState, targetState) {
  // Cancellation always requires confirmation
  if (targetState === 'cancelled') return true;

  // Transitions involving side states always require confirmation
  if (SIDE_STATES.includes(targetState)) return true;
  if (SIDE_STATES.includes(currentState)) return true;

  const currentIndex = STATE_ORDER.indexOf(currentState);
  const targetIndex = STATE_ORDER.indexOf(targetState);

  // Backward transitions always require confirmation
  if (targetIndex < currentIndex) return true;

  // Forward critical transitions
  if (currentState === 'quote' && targetState === 'planned') return true;
  if (currentState === 'planned' && targetState === 'in_progress') return true;
  if (currentState === 'in_progress' && targetState === 'completed') return true;

  // completed -> picked_up does NOT require confirmation
  return false;
}

const FINISHED_STATES = ['completed', 'picked_up'];

/**
 * Whether the work order itself has a km or hour meter reading. An earlier visit's reading on
 * the vehicle does not count: that stale value is what the completion prompt exists to catch.
 * Needs the detail serializer object (`odometer_readings`).
 */
export function hasOdometerReading(maintenance) {
  return (maintenance?.odometer_readings || []).some(
    (r) => r.reading_km != null || r.reading_hours != null
  );
}

/**
 * Whether finishing the work order (into completed, or straight to picked_up) should ask for
 * the odometer reading. Completed -> picked_up never asks: the work was already done. Mirrors
 * maintenances/services/odometer.py, which refuses the transition when the organization option
 * require_odometer_on_completion is on.
 */
export function needsOdometerPrompt(maintenance, targetState) {
  if (!maintenance) return false;
  if (!FINISHED_STATES.includes(targetState) || FINISHED_STATES.includes(maintenance.state)) return false;
  return !hasOdometerReading(maintenance);
}

/** completed|picked_up -> in_progress, the transition the invoice guards apply to. */
export function isReopenTransition(currentState, targetState) {
  return ['completed', 'picked_up'].includes(currentState) && targetState === 'in_progress';
}

/**
 * Invoice-related flags for reopen transitions, computed from the detail serializer object
 * (needs `invoices[]` with invoice_type / status / invoice_number / has_active_credit_note
 * and `has_koontilasku`). A list or calendar object lacks both and yields the
 * "nothing blocks" default, so callers must pass the detail object.
 */
export function getInvoiceReopenInfo(maintenance) {
  if (!maintenance?.invoices?.length && !maintenance?.has_koontilasku) {
    return { hasKoontilasku: false, hasDraftInvoices: false, hasBlockingInvoices: false };
  }
  const regular = (maintenance.invoices || []).filter((inv) => inv.invoice_type !== 'INV02');
  const draft = regular.filter((inv) => inv.status === 'draft');
  const blocking = regular.filter(
    (inv) => ['sent', 'paid'].includes(inv.status) && !inv.has_active_credit_note
  );
  return {
    hasKoontilasku: !!maintenance?.has_koontilasku,
    hasDraftInvoices: draft.length > 0,
    draftInvoiceNumbers: draft.map((inv) => inv.invoice_number),
    hasBlockingInvoices: blocking.length > 0,
    blockingInvoiceNumbers: blocking.map((inv) => inv.invoice_number),
  };
}

/**
 * Extra set_state body for a confirmed transition. When the confirm dialog has already
 * warned that draft invoices will be cancelled (reopen with drafts but nothing blocking),
 * the request carries cancel_draft_invoices up front instead of round-tripping a 409.
 */
export function reopenSetStateOptions(currentState, targetState, invoiceInfo) {
  if (
    isReopenTransition(currentState, targetState)
    && invoiceInfo?.hasDraftInvoices
    && !invoiceInfo.hasBlockingInvoices
    && !invoiceInfo.hasKoontilasku
  ) {
    return { cancel_draft_invoices: true };
  }
  return {};
}

/**
 * User-facing message for a failed PATCH set_state, or null when nothing should be shown.
 * A 403 with a string `detail` is already toasted by the global PermissionDeniedSnackbar
 * (src/index.js response interceptor). 409 bodies carry a translated `message`; 400 bodies a
 * translated `error`. Anything else is generic: raw exception text never reaches the user.
 */
export function setStateErrorMessage(error, t) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (status === 403 && typeof data?.detail === 'string') return null;
  if (status === 409 && typeof data?.message === 'string') return data.message;
  if (status === 400 && typeof data?.error === 'string') return data.error;
  return t('Saving failed');
}
